import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import c from 'classnames';
import {
  DEFAULT_LOCALE,
  formatDisplayableCurrency,
  interpolateStringWithData,
} from 'utils';

const getMicrocopyValues = (microcopy?: MicrocopyFragment[]) => {
  const freeMicrocopy = microcopy?.find(
    (microcopyFragment) => microcopyFragment.key === 'product-card.free'
  )?.value;
  const onSaleAriaMicrocopy = microcopy?.find(
    (microcopyFragment) =>
      microcopyFragment.key === 'product-card.on-sale-aria-label'
  )?.value;
  const originalAriaMicrocopy = microcopy?.find(
    (microcopyFragment) =>
      microcopyFragment.key === 'product-card.original-price-aria-label'
  )?.value;

  return {
    freeMicrocopy: freeMicrocopy ?? 'Free',
    onSaleAriaMicrocopy: onSaleAriaMicrocopy ?? 'On sale for: {{price}}',
    originalAriaMicrocopy: originalAriaMicrocopy ?? 'Original price: {{price}}',
  };
};

type ProductPriceProps = {
  price: {
    currentPrice: number;
    originalPrice: number;
    currency?: string;
    discountPercent?: number;
  };
  isSaleActive: boolean;
  className?: string;
  style?: 'pdp' | 'product-card';
};

export const ProductPrice = ({
  isSaleActive,
  price,
  className = '',
  style = 'product-card',
}: ProductPriceProps) => {
  const microcopy = useMicrocopy<{
    productCardMicrocopy: MicrocopyFragment[];
  }>();
  const productCardMicrocopy = microcopy?.productCardMicrocopy;
  const { freeMicrocopy, onSaleAriaMicrocopy, originalAriaMicrocopy } =
    getMicrocopyValues(productCardMicrocopy);
  const currentPriceFormatted = formatDisplayableCurrency(
    isSaleActive ? price.currentPrice : price.originalPrice,
    DEFAULT_LOCALE,
    price.currency
  );
  const originalPriceFormatted = formatDisplayableCurrency(
    price.originalPrice,
    DEFAULT_LOCALE,
    price.currency
  );

  return (
    <div
      className={c(
        'flex flex-col gap-x-2 lg:flex-row lg:items-center',
        { 'text-2xl': style === 'pdp' },
        className
      )}
    >
      {!price.currentPrice ? (
        <div data-test="product-card-current-price">{freeMicrocopy}</div>
      ) : (
        <>
          {isSaleActive ? (
            <span className="sr-only">
              {interpolateStringWithData(onSaleAriaMicrocopy, {
                price: currentPriceFormatted,
              })}
            </span>
          ) : null}
          <div
            data-test="product-card-current-price"
            aria-hidden={isSaleActive ? true : false}
            className={c({ 'text-red-600': isSaleActive })}
          >
            {currentPriceFormatted}
          </div>
        </>
      )}
      {isSaleActive ? (
        <>
          <span className="sr-only">
            {interpolateStringWithData(originalAriaMicrocopy, {
              price: originalPriceFormatted,
            })}
          </span>
          <div
            aria-hidden="true"
            data-test="product-card-original-price"
            className={c('text-sm text-grey-600 line-through lg:text-base', {
              'text-md': style === 'pdp',
            })}
          >
            {originalPriceFormatted}
          </div>
        </>
      ) : null}
    </div>
  );
};
